import { router, useFocusEffect, type Href } from 'expo-router';
import { ArrowRightIcon, CalendarClockIcon } from 'lucide-react-native';
import * as React from 'react';
import { ActivityIndicator, FlatList, Pressable, RefreshControl, View } from 'react-native';

import { useAnalytics } from '@/components/analytics-provider';
import { MemberEventFeedRow } from '@/components/member-event-feed-row';
import { MemberHeaderBar } from '@/components/member-header-bar';
import { useSession } from '@/components/session-provider';
import { useTenant } from '@/components/tenant-provider';
import { getCurrentTime } from '@/lib/dev-clock';
import {
  fetchMyActiveEventIds,
  fetchMyEventAttendance,
  type EventAttendanceStatus,
  type EventAttendanceSummary,
} from '@/lib/event-attendance';
import { cancelEventRegistration } from '@/lib/event-registration';
import { fetchEventCoverImageUrls } from '@/lib/event-assets';
import {
  cancelRsvp,
  fetchEventsByIds,
  fetchPublishedEventPage,
  isEventFull,
  subscribeToEventDataInvalidation,
  type Event,
} from '@/lib/events';
import { fetchTenantsByIds, isUserTenantAdmin, type Tenant } from '@/lib/tenant';
import { fetchBlockedUserIds } from '@/lib/moderation';
import { BRAND } from '@sixam/config/brand';
import { isEventUnderway } from '@sixam/shared/event-time';
import {
  HOME_EVENT_PAGE_SIZE,
  registeredFutureEvents,
  type EventPageCursor,
  type HomeEventFilter,
} from '@sixam/shared/home-feed-core';
import { Button } from '@sixam/ui/button';
import { Icon } from '@sixam/ui/icon';
import { Text } from '@sixam/ui/text';

type FeedEventRow = Readonly<{
  attendanceStatus: EventAttendanceStatus;
  event: Event;
  isReadOnly: boolean;
  kind: 'event';
  source: 'mine' | 'feed';
}>;

type HomeRow =
  | FeedEventRow
  | Readonly<{ id: string; kind: 'filters' }>
  | Readonly<{ event: Event; id: string; kind: 'laterToday' }>
  | Readonly<{ id: string; kind: 'message'; message: string; tone?: 'error' | 'muted' }>
  | Readonly<{ id: string; kind: 'loading' }>;

type HomeFeedLoadMode = 'background' | 'initial' | 'refresh';

function calendarDay(date: Date, timeZone: string) {
  return new Intl.DateTimeFormat('en-CA', {
    day: '2-digit',
    month: '2-digit',
    timeZone,
    year: 'numeric',
  }).format(date);
}

function matchesSearch(event: Event, query: string, tenant?: Tenant) {
  if (!query) return true;
  return [
    event.title,
    event.description,
    event.place_name,
    event.place_address,
    tenant?.name,
    tenant?.slug,
  ].some((value) => value?.toLowerCase().includes(query));
}

export default function GlobalHomeScreen() {
  const analytics = useAnalytics();
  const { isAdmin, session } = useSession();
  const { memberships, tenants } = useTenant();
  const [filter, setFilter] = React.useState<HomeEventFilter>('upcoming');
  const [searchQuery, setSearchQuery] = React.useState('');
  const [myEvents, setMyEvents] = React.useState<Event[]>([]);
  const [feedEvents, setFeedEvents] = React.useState<Event[]>([]);
  const [nextCursor, setNextCursor] = React.useState<EventPageCursor | null>(null);
  const [resolvedTenants, setResolvedTenants] = React.useState<Tenant[]>([]);
  const [attendanceByEventId, setAttendanceByEventId] = React.useState(
    new Map<string, EventAttendanceSummary>()
  );
  const [coverImageByEventId, setCoverImageByEventId] = React.useState(new Map<string, string>());
  const [unregisteringEventIds, setUnregisteringEventIds] = React.useState(new Set<string>());
  const [isLoading, setIsLoading] = React.useState(true);
  const [isLoadingMore, setIsLoadingMore] = React.useState(false);
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  // Content from a blocked member must disappear at once, so this is part of the
  // same load rather than a later filter the feed renders without.
  const [blockedUserIds, setBlockedUserIds] = React.useState<ReadonlySet<string>>(new Set());
  const requestIdRef = React.useRef(0);

  const loadEvents = React.useCallback(
    async (mode: HomeFeedLoadMode) => {
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;
      if (mode === 'initial') setIsLoading(true);
      if (mode === 'refresh') setIsRefreshing(true);
      setIsLoadingMore(false);

      try {
        const now = getCurrentTime();
        const [registeredEventIds, page, blocked] = await Promise.all([
          fetchMyActiveEventIds(),
          fetchPublishedEventPage({ filter, now }),
          fetchBlockedUserIds().catch(() => [] as readonly string[]),
        ]);
        const registeredEvents = await fetchEventsByIds(registeredEventIds);
        const nextMyEvents = registeredFutureEvents(
          registeredEvents,
          new Set(registeredEventIds),
          now
        );
        const allEvents = [...nextMyEvents, ...page.items];
        const [attendance, nextTenants, coverImages] = await Promise.all([
          fetchMyEventAttendance(allEvents.map((event) => event.id)),
          fetchTenantsByIds([
            ...new Set(
              allEvents
                .map((event) => event.tenant_id)
                .filter((tenantId): tenantId is string => Boolean(tenantId))
            ),
          ]),
          fetchEventCoverImageUrls(allEvents.map((event) => event.id)),
        ]);
        if (requestId !== requestIdRef.current) return;

        setBlockedUserIds(new Set(blocked));
        setMyEvents(nextMyEvents);
        setFeedEvents(page.items);
        setNextCursor(page.nextCursor);
        setResolvedTenants(nextTenants);
        setAttendanceByEventId(attendance);
        setCoverImageByEventId(coverImages);
        setError(null);
      } catch (loadError: unknown) {
        if (requestId !== requestIdRef.current) return;
        setError(loadError instanceof Error ? loadError.message : 'Failed to load the home feed.');
      } finally {
        if (requestId === requestIdRef.current) {
          setIsLoading(false);
          setIsRefreshing(false);
        }
      }
    },
    [filter]
  );

  useFocusEffect(
    React.useCallback(() => {
      void loadEvents('initial');
      return () => {
        requestIdRef.current += 1;
      };
    }, [loadEvents])
  );

  React.useEffect(
    () => subscribeToEventDataInvalidation(() => void loadEvents('background')),
    [loadEvents]
  );

  const loadMoreEvents = React.useCallback(async () => {
    if (!nextCursor || isLoadingMore) return;

    const requestId = requestIdRef.current;
    setIsLoadingMore(true);
    try {
      const page = await fetchPublishedEventPage({
        cursor: nextCursor,
        filter,
        limit: HOME_EVENT_PAGE_SIZE,
        now: getCurrentTime(),
      });
      const [attendance, nextTenants, coverImages] = await Promise.all([
        fetchMyEventAttendance(page.items.map((event) => event.id)),
        fetchTenantsByIds([
          ...new Set(
            page.items
              .map((event) => event.tenant_id)
              .filter((tenantId): tenantId is string => Boolean(tenantId))
          ),
        ]),
        fetchEventCoverImageUrls(page.items.map((event) => event.id)),
      ]);
      if (requestId !== requestIdRef.current) return;

      setFeedEvents((current) => [
        ...new Map([...current, ...page.items].map((event) => [event.id, event])).values(),
      ]);
      setResolvedTenants((current) => [
        ...new Map([...current, ...nextTenants].map((tenant) => [tenant.id, tenant])).values(),
      ]);
      setAttendanceByEventId((current) => new Map([...current, ...attendance]));
      setCoverImageByEventId((current) => new Map([...current, ...coverImages]));
      setNextCursor(page.nextCursor);
    } catch (loadError: unknown) {
      if (requestId === requestIdRef.current) {
        setError(loadError instanceof Error ? loadError.message : 'Failed to load more events.');
      }
    } finally {
      if (requestId === requestIdRef.current) setIsLoadingMore(false);
    }
  }, [filter, isLoadingMore, nextCursor]);

  const unregisterFromEvent = React.useCallback(
    async (eventId: string) => {
      const attendance = attendanceByEventId.get(eventId);
      if (!attendance) throw new Error('Registration details are unavailable.');

      setUnregisteringEventIds((current) => new Set(current).add(eventId));
      try {
        let didUnregister = false;
        let nextRegistration = attendance.registration;
        let nextRsvp = attendance.rsvp;

        if (attendance.registration?.status === 'confirmed') {
          nextRegistration = await cancelEventRegistration(eventId);
          didUnregister = true;
        }
        if (attendance.rsvp?.status === 'going') {
          await cancelRsvp(eventId);
          nextRsvp = { ...attendance.rsvp, status: 'cancelled' };
          didUnregister = true;
        }
        if (!didUnregister) throw new Error('This registration is no longer active.');

        setMyEvents((current) => current.filter((event) => event.id !== eventId));
        setAttendanceByEventId((current) => {
          const next = new Map(current);
          next.set(eventId, {
            ...attendance,
            registration: nextRegistration,
            rsvp: nextRsvp,
            status: 'cancelled',
          });
          return next;
        });
        setError(null);
        analytics.capture('event_registration_cancelled', { event_id: eventId });
        void loadEvents('background');
      } catch (unregisterError: unknown) {
        setError(
          unregisterError instanceof Error ? unregisterError.message : 'Could not unregister.'
        );
        analytics.capture('event_registration_cancellation_failed', { event_id: eventId });
        throw unregisterError;
      } finally {
        setUnregisteringEventIds((current) => {
          const next = new Set(current);
          next.delete(eventId);
          return next;
        });
      }
    },
    [analytics, attendanceByEventId, loadEvents]
  );

  const tenantById = React.useMemo(
    () => new Map([...resolvedTenants, ...tenants].map((tenant) => [tenant.id, tenant])),
    [resolvedTenants, tenants]
  );
  const query = searchQuery.trim().toLowerCase();
  const visibleMyEvents = myEvents.filter(
    (event) =>
      !(event.created_by && blockedUserIds.has(event.created_by)) &&
      matchesSearch(event, query, tenantById.get(event.tenant_id ?? ''))
  );
  const myEventIds = new Set(myEvents.map((event) => event.id));
  const visibleFeedEvents = feedEvents.filter(
    (event) =>
      !myEventIds.has(event.id) &&
      !(event.created_by && blockedUserIds.has(event.created_by)) &&
      matchesSearch(event, query, tenantById.get(event.tenant_id ?? ''))
  );

  /*
    Filters, then the runs they select — nothing above them and no heading
    underneath. The previous layout answered "any upcoming events?" once before
    the filter and again after it, which read as two separate lists.

    Runs you are going to are merged into the same list rather than kept in
    their own section: the row already shows attendance, so the next run is
    simply the one at the top. Past shows the feed alone, since the registered
    set only ever holds future runs.

    A full run is dropped, since there is nothing to do with it — unless it is
    already yours, because filling up must not hide the event from someone who
    is going to it.
  */
  const listedEvents =
    filter === 'upcoming'
      ? [...visibleMyEvents, ...visibleFeedEvents]
          .filter((event) => myEventIds.has(event.id) || !isEventFull(event))
          .sort((left, right) => left.starts_at.localeCompare(right.starts_at))
      : visibleFeedEvents;

  const now = getCurrentTime();
  const laterTodayEvent =
    filter === 'upcoming' && !query
      ? listedEvents.find((event) => {
          const startsAt = new Date(event.starts_at);
          const timeZone = tenantById.get(event.tenant_id ?? '')?.timezone ?? 'Asia/Bangkok';
          return startsAt > now && calendarDay(startsAt, timeZone) === calendarDay(now, timeZone);
        })
      : undefined;
  const rows: HomeRow[] = [{ id: 'feed-filters', kind: 'filters' }];
  if (laterTodayEvent) {
    rows.push({ event: laterTodayEvent, id: 'later-today', kind: 'laterToday' });
  }
  if (isLoading && feedEvents.length === 0) rows.push({ id: 'loading', kind: 'loading' });
  if (error) rows.push({ id: 'error', kind: 'message', message: error, tone: 'error' });
  if (!isLoading && !error && listedEvents.length === 0) {
    let emptyMessage = "There's no event right now. We'll notify you when the next run is planned.";
    if (query) {
      emptyMessage = 'No runs match this search.';
    } else if (filter === 'past') {
      emptyMessage = 'No past runs yet.';
    }
    rows.push({ id: 'feed-empty', kind: 'message', message: emptyMessage, tone: 'muted' });
  }
  rows.push(
    ...listedEvents.map<FeedEventRow>((event) => {
      const isMine = myEventIds.has(event.id);
      return {
        attendanceStatus:
          attendanceByEventId.get(event.id)?.status ?? (isMine ? 'registered' : 'none'),
        event,
        isReadOnly: filter === 'past',
        kind: 'event',
        source: isMine ? 'mine' : 'feed',
      };
    })
  );
  if (isLoadingMore) rows.push({ id: 'loading-more', kind: 'loading' });

  return (
    <View className="flex-1 bg-background" collapsable={false}>
      <MemberHeaderBar
        onCreatePress={session ? () => router.push('/app/events/new') : undefined}
        onSearchChange={setSearchQuery}
        searchPlaceholder="Search 6am"
      />
      <FlatList
        contentInsetAdjustmentBehavior="automatic"
        contentContainerClassName="pb-28"
        data={rows}
        keyExtractor={(item) =>
          item.kind === 'event' ? `${item.source}-${item.event.id}` : item.id
        }
        onEndReached={() => void loadMoreEvents()}
        onEndReachedThreshold={0.4}
        refreshControl={
          <RefreshControl
            colors={[BRAND.color.orange]}
            onRefresh={() => void loadEvents('refresh')}
            progressBackgroundColor={BRAND.color.smoke}
            refreshing={isRefreshing}
            tintColor={BRAND.color.orange}
          />
        }
        renderItem={({ item }) => {
          if (item.kind === 'loading') {
            return (
              <View className="items-center border-b border-border px-4 py-6">
                <ActivityIndicator color={BRAND.color.orange} />
              </View>
            );
          }
          if (item.kind === 'message') {
            return (
              <View className="border-b border-border px-4 py-4">
                <Text
                  className={item.tone === 'error' ? 'text-destructive' : undefined}
                  variant={item.tone === 'error' ? 'default' : 'muted'}
                  selectable
                >
                  {item.message}
                </Text>
              </View>
            );
          }
          if (item.kind === 'filters') {
            return (
              <View className="flex-row gap-2 border-b border-border px-4 py-4">
                {(['upcoming', 'past'] as const).map((option) => {
                  const selected = filter === option;
                  return (
                    <Pressable
                      accessibilityRole="button"
                      accessibilityState={{ selected }}
                      className={
                        selected
                          ? 'min-h-11 flex-1 items-center justify-center rounded-xl bg-primary px-4'
                          : 'min-h-11 flex-1 items-center justify-center rounded-xl border border-border bg-card px-4'
                      }
                      key={option}
                      onPress={() => setFilter(option)}
                    >
                      <Text
                        className={
                          selected ? 'font-bold uppercase text-white' : 'font-bold uppercase'
                        }
                      >
                        {option}
                      </Text>
                    </Pressable>
                  );
                })}
              </View>
            );
          }
          if (item.kind === 'laterToday') {
            return (
              <View className="gap-3 border-b border-border px-4 py-4">
                <View className="gap-1">
                  <Text variant="label" className="text-primary">
                    Later today
                  </Text>
                  <Text className="text-lg font-bold" numberOfLines={1}>
                    {item.event.title}
                  </Text>
                </View>
                <Button
                  onPress={() =>
                    router.push({
                      pathname: '/app/events/[id]',
                      params: { id: item.event.id },
                    })
                  }
                >
                  <Icon as={CalendarClockIcon} className="size-5 text-primary-foreground" />
                  <Text>Open event</Text>
                  <Icon as={ArrowRightIcon} className="size-5 text-primary-foreground" />
                </Button>
              </View>
            );
          }

          const tenant = item.event.tenant_id ? tenantById.get(item.event.tenant_id) : undefined;
          const canOperateEvent =
            isAdmin ||
            isUserTenantAdmin(memberships, item.event.tenant_id, session?.user.id ?? null);
          const eventHref: Href =
            canOperateEvent && isEventUnderway(item.event, now)
              ? {
                  pathname: '/admin/check-in/[id]',
                  params: { id: item.event.id },
                }
              : {
                  pathname: '/app/events/[id]',
                  params: { id: item.event.id },
                };
          return (
            <MemberEventFeedRow
              attendanceStatus={item.attendanceStatus}
              clubHref={
                tenant
                  ? { pathname: '/app/clubs/[tenant]', params: { tenant: tenant.slug } }
                  : undefined
              }
              clubName={tenant?.name ?? 'Personal event'}
              clubSlug={tenant?.slug}
              event={item.event}
              eventImageUrl={coverImageByEventId.get(item.event.id)}
              href={eventHref}
              isReadOnly={item.isReadOnly}
              isUnregistering={unregisteringEventIds.has(item.event.id)}
              now={now}
              onUnregister={
                item.source === 'mine' ? () => unregisterFromEvent(item.event.id) : undefined
              }
              showAttendanceActions={item.source === 'mine'}
              showStatus={item.isReadOnly}
              timeZone={tenant?.timezone ?? 'Asia/Bangkok'}
            />
          );
        }}
      />
    </View>
  );
}
